/**
 * The body of the status watcher's `GET /status` (ADR-078's amendment of 2026-10-13, decision 5), shared so the
 * Worker writes and the page reads one shape. The watcher sits outside the site and probes the gateway; it says
 * what the gateway cannot say while it is down: whether it is reachable from outside, for how long that has been
 * so, and an operator's sentence. `forMs` is relative to the watcher's clock when it answered, never an epoch, so
 * a page's wrong clock cannot move it (decision 1's rule).
 */

/**
 * The path the watcher answers its status at, and the one its notice is set and cleared at.
 */
export const WATCH_STATUS_PATH = "/status";
export const WATCH_NOTICE_PATH = "/notice";

/**
 * The most characters a notice holds.
 */
export const NOTICE_MAX = 200;

/**
 * What the watcher sees of the gateway: reachable from outside the site, or not.
 */
export type WatchState = "online" | "offline";

/**
 * The watcher's status body: the state, how long it has held in milliseconds, and the operator's notice or null.
 */
export interface WatchBody {
  readonly state: WatchState;
  readonly forMs: number;
  readonly notice: string | null;
}

const STATES: ReadonlySet<string> = new Set<WatchState>(["online", "offline"]);
const CONTROL = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/;

/**
 * Reads a notice an operator sends: text of one line, trimmed, 1 to `NOTICE_MAX` characters, with no control
 * character and no line or paragraph separator.
 *
 * @param value The value sent.
 * @returns The notice as it is kept, or null when it is not one.
 */
export function readNotice(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }
  const notice = value.trim();
  if (notice === "" || CONTROL.test(notice) || [...notice].length > NOTICE_MAX) {
    return null;
  }
  return notice;
}

/**
 * Reads a parsed watcher status body. Fields a later watcher adds are ignored; a state this reader does not
 * know, a time that is not a whole number of milliseconds from 0, or a notice that is neither null nor a notice as
 * `readNotice` keeps one makes the body unreadable.
 *
 * @param value The parsed JSON.
 * @returns The body, or null when it cannot be read.
 */
export function readWatchBody(value: unknown): WatchBody | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  const { state, forMs, notice } = value as Record<string, unknown>;
  if (typeof state !== "string" || !STATES.has(state) || typeof forMs !== "number" || !Number.isSafeInteger(forMs) || forMs < 0) {
    return null;
  }
  if (notice !== null && (typeof notice !== "string" || readNotice(notice) !== notice)) {
    return null;
  }
  return { state: state as WatchState, forMs, notice };
}
